import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings } from 'lucide-react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover';
import { NotificationBell } from '../interaction/NotificationBell';
import { NotificationFilters } from '../interaction/NotificationFilters';
import { NotificationList } from '../display/NotificationList';
import { useNotifications, useMarkAsRead, useUnreadCount, useMarkAllAsRead, useArchiveNotification } from '../../hooks';
import type { Notification, NotificationReadFilter } from '../../models/Notification.types';

function navigateFromNotificationLink(link: string, navigate: NavigateFunction) {
  const trimmed = link.trim();
  if (!trimmed) return;

  if (/^https?:\/\//i.test(trimmed)) {
    let url: URL;
    try {
      url = new URL(trimmed);
    } catch {
      return;
    }
    if (url.origin === window.location.origin) {
      navigate(`${url.pathname}${url.search}${url.hash}`);
    } else {
      window.location.assign(trimmed);
    }
  } else {
    const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    navigate(path);
  }
}

export const NotificationCenter = () => {
  const { t } = useTranslation('notificationsModule');
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [readFilter, setReadFilter] = useState<NotificationReadFilter>('unread');

  const { notifications, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useNotifications(readFilter);

  const { count: unreadCount } = useUnreadCount();
  const markAsReadMutation = useMarkAsRead();
  const markAllAsReadMutation = useMarkAllAsRead();
  const archiveMutation = useArchiveNotification();

  const handleMarkAsRead = useCallback(
    (id: number) => {
      markAsReadMutation.mutate(id);
    },
    [markAsReadMutation],
  );

  const handleNotificationOpen = useCallback(
    (notification: Notification) => {
      if (!notification.read_at) {
        handleMarkAsRead(notification.id);
      }
      if (notification.link) {
        navigateFromNotificationLink(notification.link, navigate);
      }
      setIsOpen(false);
    },
    [handleMarkAsRead, navigate],
  );

  const handleArchive = useCallback(
    (id: number) => {
      archiveMutation.mutate(id);
    },
    [archiveMutation],
  );

  const handleSettingsClick = () => {
    setIsOpen(false);
    navigate('/configuration/notifications');
  };

  const handleMarkAllRead = () => {
    markAllAsReadMutation.mutate();
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <NotificationBell />
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        className="w-[calc(100vw-2rem)] sm:w-[480px] p-0 bg-body-bg border border-card-border rounded-lg shadow-lg"
      >
        <div className="px-6 py-4 flex flex-col gap-4">
          <div className="flex justify-between items-center gap-2">
            <span className="text-text text-base font-medium leading-5">{t('notifications.title')}</span>
            <div className="flex items-center gap-1 shrink-0">
              {readFilter === 'unread' && unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  disabled={markAllAsReadMutation.isPending}
                  className="text-xs font-medium text-primary hover:underline px-2 py-1 rounded disabled:opacity-50"
                  data-testid="notification-mark-all-read"
                >
                  {markAllAsReadMutation.isPending
                    ? t('notifications.actions.markingAllRead')
                    : t('notifications.actions.markAllRead')}
                </button>
              )}
              <button
                onClick={handleSettingsClick}
                className="p-1 text-icons hover:text-text transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title={t('notifications.actions.settings')}
                data-testid="notification-settings"
                type="button"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>
          </div>

          <NotificationFilters readFilter={readFilter} onReadFilterChange={setReadFilter} />
        </div>

        <div className="px-3 pb-4">
          <NotificationList
            notifications={notifications}
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage ?? false}
            readFilter={readFilter}
            onFetchNextPage={fetchNextPage}
            onNotificationOpen={handleNotificationOpen}
            onMarkAsReadOnly={handleMarkAsRead}
            onArchive={handleArchive}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};
